import { useState } from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';

import PricePerUnitLegalModal from 'features/shop/PricePerUnitLegalModal';

/**
 * Normalizes and rounds the price per unit to 2 decimal places.
 *
 * @param {number} pricePerUnit Price per unit
 * @returns {string} Normalized and rounded price per unit
 */
function normalizeAndRoundPricePerUnit(pricePerUnit) {
    // Below we add 0.005 to the price per unit to prevent rounded amounts
    // from being too low, for legal safety reasons.
    return (pricePerUnit + 0.005).toFixed(2);
}

/**
 * Gets the text copy for "Estimated" or "Est." depending on desktop or mobile
 *
 * @param {string} copyType Copy type ('abbreviated' or 'full')
 * @returns {string} Estimated text copy
 */
function getEstimatedTextCopy(copyType) {
    return copyType === 'full' ? 'Estimated ' : 'Est. ';
}

/**
 * Gets the price per unit text content.
 *
 * @param {number|number[]} pricePerUnitRatio Price per unit ratio
 * @param {number} price Price
 * @param {number} portion Portion
 * @returns {string} Price per unit text content
 */
function getPricePerUnitTextContent(pricePerUnitRatio, price, portion) {
    if (Array.isArray(pricePerUnitRatio)) {
        return pricePerUnitRatio
            .map((ratio) => `$${normalizeAndRoundPricePerUnit((ratio * price) / portion)}`)
            .join(' - ');
    }
    return `$${normalizeAndRoundPricePerUnit((pricePerUnitRatio * price) / portion)}`;
}

/**
 * Displays a modal and a trigger button for the price per unit legal modal.
 * @returns {JSX.Element} Modal trigger
 */
function ModalTrigger() {
    const [showPricePerUnitLegalModal, setShowPricePerUnitLegalModal] = useState(false);

    const modalOnClose = (e) => {
        e.stopPropagation(); // Prevent PDP from opening when closing modal
        setShowPricePerUnitLegalModal(false);
    };

    return (
        <>
            <button
                type="button"
                onClick={(e) => {
                    e.stopPropagation(); // Prevent PDP from opening when clicking the modal trigger
                    setShowPricePerUnitLegalModal(true);
                }}
                aria-label="How is this calculated?"
                className="align-middle"
            >
                <InformationCircleIcon className="w-20" />
            </button>
            {showPricePerUnitLegalModal && (
                <PricePerUnitLegalModal open={showPricePerUnitLegalModal} onClose={modalOnClose} />
            )}
        </>
    );
}

/**
 * Displays price per unit information.
 *
 * @param {Object} props Component props
 * @param {string} props.copyType Copy type ('abbreviated' or 'full')
 * @param {boolean} props.isEstimated Whether the price per unit is estimated
 * @param {number} props.portion Portion of the product (bundles only)
 * @param {number} props.price Price
 * @param {string} props.pricePerUnitBase Price per unit base unit of measure (typically oz)
 * @param {number|number[]} props.pricePerUnitRatio Ratio to multiply price by to get price per unit
 * @returns {JSX.Element|null}
 */
function PricePerUnitInfo({ copyType, isEstimated, portion = 1, price, pricePerUnitBase, pricePerUnitRatio }) {
    let textContent = '';
    if (Array.isArray(pricePerUnitRatio)) {
        // Hide for now until we figure out how to handle large ranges of price per unit ratios
        return null;
        // const reversedPricePerUnitRatio = pricePerUnitRatio.toReversed();
        // textContent = `${reversedPricePerUnitRatio.map((ratio) => getPricePerUnitTextContent(ratio, price, portion)).join(' - ')}`;
    }
    const pricePerUnit = (pricePerUnitRatio * price) / portion;
    if (!isEstimated && (pricePerUnit > price || (pricePerUnitRatio === 1 && portion === 1))) {
        // Do not show if price per unit is greater than overall price or if price per unit ratio is 1
        return null;
    }
    textContent = getPricePerUnitTextContent(pricePerUnitRatio, price, portion);

    return (
        <p className="text-body-sm text-black">
            {isEstimated ? getEstimatedTextCopy(copyType) : ''}
            {textContent} / {pricePerUnitBase}
            {isEstimated && (
                <>
                    {' '}
                    <ModalTrigger />
                </>
            )}
        </p>
    );
}

export default PricePerUnitInfo;
